import { useState, useCallback } from 'react';
import { QueryKey } from 'react-query';

import { TeamNewFinStatusesEnum } from '../../../../../../../main/teams/teamsTypes';

import {
  UniversalFilterCompanyNewFinStatusValue,
  UniversalFilterCompanyNewFinStatusModes,
  UniversalFilterCompanyNewFinStatusFilterState,
  UniversalFilterCompanyNewFinStatusOnFilterStateChange,
  UniversalFilterCompanyNewFinStatusOnChange
} from '../../UniversalFilterCompanyNewFinStatus.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterCompanyNewFinStatusFilterState } from '../useUniversalFilterCompanyNewFinStatusFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterCompanyNewFinStatusModes.IS_ANY_OF;

const modeOptions: UniversalFilterTemplateModeOption<UniversalFilterCompanyNewFinStatusModes>[] =
  [
    {
      value: UniversalFilterCompanyNewFinStatusModes.IS_ANY_OF,
      i18nText: filtersKeys.modes.isAnyOf
    },
    {
      value: UniversalFilterCompanyNewFinStatusModes.IS_NONE_OF,
      i18nText: filtersKeys.modes.isNoneOf
    }
  ];

interface UniversalFilterCompanyNewFinStatusOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterCompanyNewFinStatusValue;
  onChange: UniversalFilterCompanyNewFinStatusOnChange;
}

function useUniversalFilterCompanyNewFinStatus({
  filterStateCacheKey,
  filterValue,
  onChange
}: UniversalFilterCompanyNewFinStatusOptions) {
  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterCompanyNewFinStatusFilterState>(
      filterStateCacheKey
    );

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterCompanyNewFinStatusFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterCompanyNewFinStatusFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: defaultMode,
          statuses: []
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterCompanyNewFinStatusOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterCompanyNewFinStatusModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        statuses: filterState.statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleStatusesChange = useCallback<
    (statuses: TeamNewFinStatusesEnum[]) => void
  >(
    (statuses) => {
      handleFilterStateChange({
        mode: filterState.mode,
        statuses: statuses || []
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleStatusesChange
  };
}

export default useUniversalFilterCompanyNewFinStatus;
